import { Constants } from '../../../../utils/constants';
import { formatCount, formatNaira } from '../../../../utils/money';
import { formatLagos } from '../../../../utils/datetime';
import {
  CopyButton,
  DataTable,
  DateRangeFilter,
  FilterSelect,
  Pagination,
  SearchInput,
  StatusPill,
  TableCard,
} from '../../../ui';
import { useServerTable, wholeDayDates } from '../../../../hooks/useServerTable';
import SubMerchantDeactivated, { isSubMerchantDeactivated } from './SubMerchantDeactivated';

const Destination = ({ destination }) => {
  if (!destination?.account_number) return '—';
  return (
    <div className="whitespace-nowrap">
      <div className="font-medium text-theme-headings dark:text-themedark-headings">{destination.account_name || '—'}</div>
      <div className="text-sm text-theme-secondarytextcolor dark:text-themedark-secondarytextcolor">
        {[destination.bank_name, destination.account_number].filter(Boolean).join(' · ')}
      </div>
    </div>
  );
};

const Reference = ({ reference }) => {
  if (!reference) return '—';
  return (
    <div className="flex items-center gap-2 whitespace-nowrap">
      <span>{reference}</span>
      <CopyButton text={reference} label="Copy" />
    </div>
  );
};

const WithdrawalsTable = ({ endpoint }) => {
  const table = useServerTable(endpoint, {
    sort: '-created_at',
    filters: { status: '' },
    dateParams: wholeDayDates,
    fallbackError: 'We could not load the withdrawals. Please try again.',
  });

  if (isSubMerchantDeactivated(table.requestError)) return <SubMerchantDeactivated />;

  const columns = [
    {
      key: 'created_at',
      header: 'Date (WAT)',
      sortKey: 'created_at',
      sortDefault: 'desc',
      render: (row) => <span className="whitespace-nowrap">{formatLagos(row.created_at)}</span>,
      filter: (
        <DateRangeFilter
          label="Date"
          from={table.from}
          to={table.to}
          onFromChange={table.setFrom}
          onToChange={table.setTo}
        />
      ),
    },
    { key: 'reference', header: 'Reference', render: (row) => <Reference reference={row.reference} /> },
    { key: 'destination', header: 'Destination', render: (row) => <Destination destination={row.destination} /> },
    {
      key: 'amount',
      header: 'Amount',
      align: 'right',
      sortKey: 'amount',
      sortDefault: 'desc',
      render: (row) => formatNaira(row.amount),
    },
    { key: 'fee', header: 'Fee', align: 'right', render: (row) => formatNaira(row.fee) },
    {
      key: 'status',
      header: 'Status',
      render: (row) => <StatusPill status={row.status} />,
      filter: (
        <FilterSelect
          value={table.filters.status ?? ''}
          onChange={(value) => table.setFilter('status', value)}
          options={Constants.withdrawalStatuses}
          label="All"
          ariaLabel="Filter by status"
        />
      ),
    },
    {
      key: 'updated_at',
      header: 'Updated (WAT)',
      render: (row) => <span className="whitespace-nowrap">{formatLagos(row.updated_at)}</span>,
    },
  ];

  return (
    <TableCard
      title="Withdrawals"
      summary={{ label: 'Withdrawals', value: formatCount(table.total), loading: table.loading }}
      toolbar={
        <SearchInput
          value={table.search}
          onChange={table.setSearch}
          placeholder="Search account number, account name or email"
        />
      }
      footer={
        table.total > 0 && (
          <Pagination
            page={table.page}
            pageCount={table.pageCount}
            pageSize={table.pageSize}
            onPageChange={table.setPage}
            onPageSizeChange={table.setPageSize}
          />
        )
      }
    >
      <DataTable
        columns={columns}
        rows={table.rows}
        rowKey={(row) => row.id}
        serial
        serialStart={table.page * table.pageSize}
        loading={table.loading}
        error={table.error}
        invalidDateRange={table.invalidDateRange}
        sort={table.sort}
        onSortChange={table.setSort}
        empty={{ title: 'No withdrawals', message: 'No withdrawals match the selected filters.' }}
      />
    </TableCard>
  );
};

export default WithdrawalsTable;
